import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CountrySelect, StateSelect } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import axios from "axios";
import { useCart } from "../CartContext/CartContextComp"; // Import useCart
import "../../assets/css/CheckoutPage.css";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CheckoutPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { cartId, clearCart } = useCart(); // Get cartId from CartContext
    const cartItems = location.state?.cartItems || []; // Ensure cartItems is always an array

    const [country, setCountry] = useState(null);
    const [currentState, setCurrentState] = useState(null);
    const [billingCountry, setBillingCountry] = useState(null);
    const [billingState, setBillingState] = useState(null);

    const [formData, setFormData] = useState({
        email: "",
        firstName: "",
        lastName: "",
        address: "",
        apartment: "", // Added apartment field
        city: "",
        state: "",
        pinCode: "",
        phone: "",
        paymentMethod: "razorpay",
        country: "",
        billingAddress: "same",
        billingFirstName: "",
        billingLastName: "",
        billingAddressLine: "",
        billingApartment: "", // Added billingApartment field
        billingCity: "",
        billingCountry: "",
        billingState:"",
        billingPinCode: "",
        billingPhone: "",
    });

    const [errors, setErrors] = useState({
        email: "",
        phone: "",
        pinCode: "",
        billingPhone: "",
        billingPinCode: "",
    });

    // Regex for validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/; // Starts with 6-9 and has 10 digits
    const pinCodeRegex = /^\d{6}$/; // Exactly 6 digits

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Restrict input for phone and PIN code fields
        let newValue = value;
        if (name === "phone" || name === "billingPhone") {
            // Allow only digits and limit to 10 characters
            newValue = value.replace(/\D/g, "").slice(0, 10);
        } else if (name === "pinCode" || name === "billingPinCode") {
            // Allow only digits and limit to 6 characters
            newValue = value.replace(/\D/g, "").slice(0, 6);
        } else if (name === "firstName" || name === "lastName" || name === "billingFirstName" || name === "billingLastName") {
            // Limit to 50 characters
            newValue = value.slice(0, 50);
        }

        // Update form data
        setFormData({ ...formData, [name]: type === "checkbox" ? checked : newValue });

        // Validation logic
        if (name === "email") {
            setErrors({ ...errors, email: emailRegex.test(value) ? "" : "Invalid email" });
        }
        if (name === "phone" || name === "billingPhone") {
            setErrors({ ...errors, [name]: phoneRegex.test(newValue) ? "" : "Invalid phone number" });
        }
        if (name === "pinCode" || name === "billingPinCode") {
            setErrors({ ...errors, [name]: pinCodeRegex.test(newValue) ? "" : "Invalid PIN code" });
        }
    };


    const isFormValid =
        emailRegex.test(formData.email) &&
        (formData.billingAddress === "same"
            ? // Validate shipping fields if billing is same
            phoneRegex.test(formData.phone) &&
            pinCodeRegex.test(formData.pinCode) &&
            formData.firstName &&
            formData.lastName &&
            formData.address &&
            formData.country &&
            formData.state &&
            formData.city
            : // Validate billing fields if billing is different
            phoneRegex.test(formData.billingPhone) &&
            pinCodeRegex.test(formData.billingPinCode) &&
            formData.billingFirstName &&
            formData.billingLastName &&
            formData.billingAddressLine &&
            formData.billingCountry &&
            formData.billingState &&
            formData.billingCity);


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid) {
            toast.error("Please fill all required fields correctly!");
            return;
        }

        const guestToken = localStorage.getItem("guest_token");
        if (!guestToken) {
            toast.error("Please refresh the page.");
            return;
        }

        try {
            const checkoutData = createOrderPayload();
            // console.log('cartid', cartId);
            // console.log("Checkout Payload:", checkoutData);

            const response = await axios.post(
                "https://admin.utharamenon.com/api/guest/create-order",
                checkoutData,
                {
                    headers: {
                        guest_token: guestToken,
                        "Content-Type": "application/json",
                    },
                }
            );

            const orderData = response.data;

            // Display success toast
            // toast.success("Order placed successfully!");

            // Optionally, clear the cart and redirect to a success page
            clearCart();
            navigate("/success", { state: { orderId: orderData.order_id } }); //  replace with your success page
        } catch (error) {
            console.error("Error placing order:", error.response?.data || error.message);
            toast.error("Failed to place order. Please try again.");
        }
    };

    const createOrderPayload = () => {
        return {
            cart_id: cartId,
            payment_method: "razorpay",
            email: formData.email,
            same_as_shipping: formData.billingAddress === "same" ? 1 : 0, // 1 if billing is same as shipping

            shipping_address:
            {
                first_name: formData.firstName ? formData.firstName : formData.billingFirstName,
                last_name: formData.lastName ? formData.lastName : formData.billingLastName,
                address_1: formData.address ? formData.address : formData.billingAddressLine,
                address_2: formData.apartment ? formData.apartment : formData.billingApartment || "",
                city: formData.city ? formData.city : formData.billingCity,
                state: formData.state ? formData.state : formData.billingState,
                country: formData.country ? formData.country : formData.billingCountry,
                pincode: formData.pinCode ? formData.pinCode : formData.billingPinCode,
                phone: formData.phone ? formData.phone : formData.billingPhone,
            },

            billing_address: formData.billingAddress === "same"
                ? {} // Empty object when billing is same as shipping
                : {
                    first_name: formData.billingFirstName,
                    last_name: formData.billingLastName,
                    address_1: formData.billingAddressLine,
                    address_2: formData.billingApartment || "",
                    city: formData.billingCity,
                    state: formData.billingState,
                    country: formData.billingCountry,
                    pincode: formData.billingPinCode,
                    phone: formData.billingPhone,
                },
        };

    };


    return (
        <div className="checkout-classname-container">
            <button className="collection-button-back" onClick={() => navigate(-1)}>
            &lt; Back
            </button>
            <div className="row">
                {/* Left Section - Contact & Delivery Details */}
                <div className="col-md-8 checkout-classname-left">
                    <div className="checkout-classname-section">
                        <h2>Contact</h2>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="checkout-classname-input-field"
                            placeholder="Email"
                            required
                        />
                        {errors.email && <p className="error-text">{errors.email}</p>}
                    </div>

                    <div className="checkout-classname-section">
                        <h2>Delivery</h2>

                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="checkout-classname-input-field"
                            placeholder="First name"
                            maxLength={30}
                            required
                        />
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="checkout-classname-input-field"
                            placeholder="Last name"
                            maxLength={30}
                            required
                        />

                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            className="checkout-classname-input-field"
                            placeholder="Address"
                            required
                        />
                        <input
                            type="text"
                            name="apartment"
                            value={formData.apartment}
                            onChange={handleChange}
                            className="checkout-classname-input-field"
                            placeholder="Apartment, suite, etc. (optional)"
                        />

                        <div className="checkout-classname-row">
                            <input
                                type="text"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                className="checkout-classname-input-field"
                                placeholder="Enter Country"
                                required
                            />

                            {/* State Input - User enters manually */}
                            <input
                                type="text"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                className="checkout-classname-input-field"
                                placeholder="Enter State"
                                required
                            />
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                className="checkout-classname-input-field"
                                placeholder="City"
                                required
                            />
                        </div>

                        <div className="checkout-classname-row">
                            <input
                                type="text"
                                name="pinCode"
                                value={formData.pinCode}
                                onChange={handleChange}
                                className="checkout-classname-input-field checkout-classname-col"
                                placeholder="PIN Code"
                                maxLength={6} // Restrict to 10 digits
                                required
                            />
                            {errors.pinCode && <p className="error-text">{errors.pinCode}</p>}

                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="checkout-classname-input-field"
                                placeholder="Phone"
                                maxLength={10} // Restrict to 10 digits
                                required
                            />
                            {errors.phone && <p className="error-text">{errors.phone}</p>}
                        </div>
                    </div>

                    {/* Payment Section */}
                    {/* <div className="checkout-classname-section">
                        <h2>Payment</h2>
                        <p>All transactions are secure and encrypted.</p>
                        <div className="checkout-classname-payment-methods">
                            <label className="checkout-classname-payment-option">
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="razorpay"
                                    onChange={handleChange}
                                    checked={formData.paymentMethod === "razorpay"}
                                />
                                <div className="checkout-classname-payment-details">
                                    <strong>Razorpay Secure (UPI, Cards, Wallets, NetBanking)</strong>
                                    <p>
                                        After clicking “Pay now”, you will be redirected to Razorpay Secure (UPI, Cards, Wallets, NetBanking) to complete your purchase securely.
                                    </p>
                                </div>
                            </label>
                        </div>
                    </div> */}

                    {/* Billing Address Section */}
                    <div className="checkout-classname-section">
                        <h2>Billing Address</h2>
                        <div className="checkout-classname-billing-options">
                            <label className="checkout-classname-billing-option">
                                <input
                                    type="radio"
                                    name="billingAddress"
                                    value="same"
                                    onChange={handleChange}
                                    checked={formData.billingAddress === "same"}
                                />
                                <span>Same as shipping address</span>
                            </label>
                            <label className="checkout-classname-billing-option">
                                <input
                                    type="radio"
                                    name="billingAddress"
                                    value="different"
                                    onChange={handleChange}
                                    checked={formData.billingAddress === "different"}
                                />
                                <span>Use a different billing address</span>
                            </label>
                        </div>

                        {formData.billingAddress === "different" && (
                            <div>
                                <input type="text" className="checkout-classname-input-field" name="billingFirstName" maxLength={30} value={formData.billingFirstName} onChange={handleChange} placeholder="First name" required />
                                <input type="text" className="checkout-classname-input-field" name="billingLastName" maxLength={30} value={formData.billingLastName} onChange={handleChange} placeholder="Last name" required />
                                <input type="text" className="checkout-classname-input-field" name="billingAddressLine" value={formData.billingAddressLine} onChange={handleChange} placeholder="Address" required />
                                <input
                                    type="text"
                                    name="billingApartment"
                                    value={formData.billingApartment}
                                    onChange={handleChange}
                                    className="checkout-classname-input-field"
                                    placeholder="Apartment, suite, etc. (optional)"
                                />
                                <div className="checkout-classname-row">
                                    <input
                                        type="text"
                                        name="billingCountry"
                                        value={formData.billingCountry}
                                        onChange={handleChange}
                                        className="checkout-classname-input-field"
                                        placeholder="Enter Billing Country"
                                        required
                                    />

                                    <input
                                        type="text"
                                        name="billingState"
                                        value={formData.billingState}
                                        onChange={handleChange}
                                        className="checkout-classname-input-field"
                                        placeholder="Enter Billing State"
                                        required
                                    />
                                    <input type="text" name="billingCity" value={formData.billingCity} onChange={handleChange} className="checkout-classname-input-field" placeholder="City" required />
                                </div>
                                <input type="text" className="checkout-classname-input-field checkout-classname-col" name="billingPinCode" maxLength={6} value={formData.billingPinCode} onChange={handleChange} placeholder="PIN Code" required />
                                {errors.billingPinCode && <p className="error-text">{errors.billingPinCode}</p>}
                                <input type="tel" className="checkout-classname-input-field" name="billingPhone" maxLength={10} value={formData.billingPhone} onChange={handleChange} placeholder="Phone" required />
                                {errors.billingPhone && <p className="error-text">{errors.billingPhone}</p>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Section - Order Summary */}
                <div className="col-md-4 checkout-classname-right">
                    <div className="checkout-classname-order-summary">
                        {cartItems.length > 0 ? (
                            cartItems.map((item, index) => (
                                <div key={index} className="checkout-classname-cart-item">
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="checkout-classname-product-image"
                                    />
                                    <div className="checkout-classname-item-details">
                                        <p>{item.name}</p>
                                        <p>Size: {item.size}</p>
                                        <p>Quantity: {item.quantity}</p>
                                        <p>Rs {(item.price * item.quantity).toFixed(2)}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p>No items in cart.</p>
                        )}
                    </div>
                    <div className="checkout-classname-total-section">
                        <p>
                            Total: Rs
                            {cartItems
                                .reduce((sum, item) => sum + item.price * item.quantity, 0)
                                .toFixed(2)}
                        </p>

                        <button className="checkout-classname-apply-btn" onClick={handleSubmit}>
                            Pay
                        </button>
                        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeButton={false} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;